import React, { useState } from 'react';
import { ContentMembership } from '@optimus/models';
import { useAppSelector } from '../../../store/hooks';
import { selectMemberInformation } from '../../../store/slices/onboardingSlice';
import * as dashboardAPI from '../../../api/dashboardAPI';
import { isSalesAssistedContentMembership } from '../../../common/utils/MembershipTierAccess';
import { MetaItem } from '../../../common/models/content/Content';
import { Logger } from '../../../config/logging/logger';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { useUpgradeNavigation } from '../../../common/hooks/useUpgradeNavigation';
import { ContentEventType } from '../../../common/constants';
import { extractCategories } from '../../../common/utils/contentUtils';

const logger = Logger.create('useLockedContentAction');

interface LockedContentDetails {
  contentId: number;
  contentTitle?: string | null;
  contentCreatorName?: string | null;
  type?: string | null;
  metadata?: MetaItem[];
  lowestMembershipRequired?: ContentMembership;
}

/**
 * What clicking a piece of locked content does, shared by the feed's ContentCard and the
 * home page highlights: offer the upgrade, or a sales conversation when the content needs a
 * membership that is not sold in app (SN-996). Render `ContentContactSalesDialog` with
 * `contactSalesDialogProps` alongside the card.
 */
export function useLockedContentAction({
  contentId,
  contentTitle,
  contentCreatorName,
  type,
  metadata,
  lowestMembershipRequired,
}: LockedContentDetails) {
  const { trackCustomEvent } = useCustomEventTracking();
  const { navigateToUpgrade } = useUpgradeNavigation();
  const { company } = useAppSelector(selectMemberInformation);
  const [openContactSales, setOpenContactSales] = useState(false);
  const [submittingContactSales, setSubmittingContactSales] = useState(false);
  const [hasContactedSales, setHasContactedSales] = useState(false);

  /**
   * Advanced and Alliance are not sold in app, so content requiring either asks the
   * member to contact sales instead of offering an upgrade, per the Content chart on
   * product's "SN Logic on the Edge platform" page (SN-996). `locked` already means
   * this member does not reach the content, so only the content's own requirement is
   * left to ask about.
   */
  const requiresSalesContact = isSalesAssistedContentMembership(lowestMembershipRequired);

  const handleUpgradeClick = (e: React.SyntheticEvent) => {
    e.stopPropagation();

    // Track the upgrade CTA click as a dedicated event: Content-Upgrade-Click-CTA
    if (contentId && type) {
      // Extract content metadata categories (topics, business goals, etc.)
      const categories = extractCategories(metadata || []);

      trackCustomEvent('Content', 'Upgrade', 'Click', 'CTA', {
        optimusEventType: ContentEventType,
        detail: {
          contentId,
          contentTitle: contentTitle || '',
          contentCreatorName: contentCreatorName || '',
          type: type || '',
          lowestMembershipRequired,
          ...categories,
        },
      });
    } else {
      logger.warn(Logger.format('Upgrade clicked but Heap tracking event not fired - missing contentId or contentType', {
        contentId,
        contentType: type,
      }));
    }

    navigateToUpgrade();
  };

  const handleContactSalesClick = (e: React.SyntheticEvent) => {
    e.stopPropagation();

    // Tracked alongside the upgrade CTA's event so the two calls to action stay
    // comparable, distinguished by `lowestMembershipRequired` in the detail.
    if (contentId && type) {
      const categories = extractCategories(metadata || []);

      trackCustomEvent('Content', 'ContactSales', 'Click', 'CTA', {
        optimusEventType: ContentEventType,
        detail: {
          contentId,
          contentTitle: contentTitle || '',
          contentCreatorName: contentCreatorName || '',
          type: type || '',
          lowestMembershipRequired,
          ...categories,
        },
      });
    } else {
      logger.warn(Logger.format('Contact Sales clicked but Heap tracking event not fired - missing contentId or contentType', {
        contentId,
        contentType: type,
      }));
    }

    setOpenContactSales(true);
  };

  /**
   * Sends the lead through the company-scoped contact-sales endpoint. There is no
   * partner behind a content request, so sales receive the member's profile details
   * with the partner lines dropped from the email (SN-996).
   */
  const handleContactSalesConfirm = async () => {
    if (!company?.id || submittingContactSales) return;

    trackCustomEvent('Content', 'ContactSales', 'Submit', 'CTA', {
      optimusEventType: ContentEventType,
      detail: {
        contentId,
        contentTitle: contentTitle || '',
        contentCreatorName: contentCreatorName || '',
        type: type || '',
        lowestMembershipRequired,
      },
    });

    setSubmittingContactSales(true);
    try {
      await dashboardAPI.requestCompanyContactSales(company.id);
      setHasContactedSales(true);
      setOpenContactSales(false);
    } catch (error) {
      // Keep the dialog open so the member can retry.
      logger.error(Logger.format('Failed to send content contact sales request', {
        contentId,
        error: (error as Error)?.message,
      }));
    } finally {
      setSubmittingContactSales(false);
    }
  };

  // What a locked card does when clicked: ask for a sales conversation when the content
  // needs a membership that is not sold in app, otherwise offer the upgrade (SN-996).
  const handleLockedClick = requiresSalesContact ? handleContactSalesClick : handleUpgradeClick;

  return {
    requiresSalesContact,
    hasContactedSales,
    handleLockedClick,
    contactSalesDialogProps: {
      open: openContactSales,
      onClose: () => setOpenContactSales(false),
      onConfirm: handleContactSalesConfirm,
      submitting: submittingContactSales,
    },
  };
}
